import { selectNodeAtPos } from '@atlaskit/editor-common/node-selection';
import type { EditorCommand, ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import type { BlockControlsPlugin } from '@atlaskit/editor-plugin-block-controls';
import type { PasteOptionsToolbarPlugin } from '@atlaskit/editor-plugin-paste-options-toolbar';
import { TextSelection } from '@atlaskit/editor-prosemirror/state';
import { fg } from '@atlaskit/platform-feature-flags';
import { expValNoExposure } from '@atlaskit/tmp-editor-statsig/expVal';

import { malleableUiPluginKey } from '../pm-plugins/plugin-key';
import { getHeadingSection } from '../remix-heading-section';
import type { RemixModalMetadata } from '../types';

export type OpenRemixModalOptions = {
	metadata?: RemixModalMetadata;
	nodePos?: number;
	nodeType?: string;
	source?: string;
};

const BLOCK_NUDGE_SOURCE = 'blockNudge';
export const PASTE_MENU_SOURCE = 'pasteMenu';

const applyHeadingSectionSelection = ({
	nodePos,
	source,
	tr,
}: {
	nodePos: number;
	source?: string;
	tr: Parameters<EditorCommand>[0]['tr'];
}): boolean => {
	if (source !== BLOCK_NUDGE_SOURCE) {
		return false;
	}

	const headingSection = getHeadingSection(tr.doc, nodePos);
	if (!headingSection) {
		return false;
	}

	const headingSectionSelection = TextSelection.create(
		tr.doc,
		headingSection.startPos,
		headingSection.endPos,
	);
	tr.setSelection(headingSectionSelection);
	return true;
};

/**
 * Opens the remix modal and, if a node position and type are provided, updates
 * the editor selection to reflect the clicked node.
 *
 * Uses `tr.selection` as the current selection at dispatch time.
 */
export const makeOpenRemixModal =
	(
		api:
			| (ExtractInjectionAPI<BlockControlsPlugin> &
					Partial<ExtractInjectionAPI<PasteOptionsToolbarPlugin>>)
			| undefined,
	) =>
	({ nodePos, nodeType, metadata, source }: OpenRemixModalOptions = {}): EditorCommand =>
	({ tr }) => {
		// If the remix modal is being opened from the paste menu, set the selection to the pasted content range
		if (
			source === PASTE_MENU_SOURCE &&
			api &&
			'pasteOptionsToolbarPlugin' in api &&
			api.pasteOptionsToolbarPlugin &&
			['hasSpellingAndGrammar', 'hasAltAiActions'].includes(
				expValNoExposure('platform_editor_paste_actions_menu_v2', 'variant', 'control'),
			)
		) {
			const pasteState = api.pasteOptionsToolbarPlugin.sharedState.currentState();
			if (!pasteState) {
				// Exit early if we can't get the paste state, which means we won't be able to set the selection to the pasted content.
				return null;
			}
			const { pasteStartPos, pasteEndPos } = pasteState;
			if (pasteStartPos === undefined || pasteEndPos === undefined) {
				// Exit early if the paste state doesn't have valid start or end positions.
				return null;
			}
			const pasteSelection = TextSelection.create(tr.doc, pasteStartPos, pasteEndPos);
			tr.setSelection(pasteSelection);
			api?.selection?.commands?.setBlockSelection(pasteSelection)({ tr });
		} else if (nodePos !== undefined && nodeType) {
			if (fg('cc-maui-phase-3')) {
				const didApplyHeadingSelection = applyHeadingSectionSelection({
					nodePos,
					source,
					tr,
				});

				if (!didApplyHeadingSelection) {
					selectNodeAtPos(tr, nodePos, nodeType);
				}
			} else if (fg('platform_editor_maui_jira_updates')) {
				selectNodeAtPos(tr, nodePos, nodeType);
			} else {
				api?.blockControls?.commands.expandAndUpdateSelection({
					startPos: nodePos,
					nodeType,
					selection: tr.selection,
					isShiftPressed: false,
				})({ tr });
			}

			// If a multi-block TextSelection was set, signal to the selection plugin
			// so that ak-editor-selected-node decorations are applied to all selected nodes.
			if (tr.selection instanceof TextSelection) {
				api?.selection?.commands?.setBlockSelection(tr.selection)({ tr });
			}
		}
		tr.setMeta(malleableUiPluginKey, {
			isRemixModalOpen: true,
			remixModalMetadata: metadata,
			remixSource: source,
		});
		return tr;
	};
